import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { supabase } from '../lib/supabase.ts';
import { Gomb, HibaUzenet, Kartya, Mezo, Parbeszed, Valaszto } from '../ui/elemek.tsx';
import type { LeadSor } from './AdatokPanel.tsx';

// Az adatbázis-függvény ok-készlete (lead_torles); a sorrend a felületi sorrend.
const OKOK = [
  { kod: 'teszt', cimke: 'Saját teszt' },
  { kod: 'iroda_kerte', cimke: 'Az iroda kérte' },
  { kod: 'erintett_kerte', cimke: 'Az érdeklődő kérte (törlési kérelem)' },
  { kod: 'egyeb', cimke: 'Egyéb' },
] as const;
type Ok = (typeof OKOK)[number]['kod'];

/** Csak a super adminnak jelenik meg (a hívó dönt); az írást az adatbázis függvénye is csak neki engedi. */
export default function LeadTorles({ lead }: { lead: LeadSor }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [nyitva, setNyitva] = useState(false);
  const [ok, setOk] = useState<Ok | ''>('');
  const [megjegyzes, setMegjegyzes] = useState('');
  const [mezoHiba, setMezoHiba] = useState<string | null>(null);

  const torles = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('lead_torles', { p_lead_id: lead.id, p_ok: ok, p_megjegyzes: megjegyzes.trim() || undefined });
      if (error) throw error;
    },
    onSuccess: async () => {
      // Előbb elnavigálunk (a data router navigate-je a navigáció végén old fel), és csak utána takarítunk: a még mountolt
      // adatlap ne kérdezze újra a törölt leadet (különben egy villanásra „nincs ilyen lead” látszana).
      await navigate('/leadek', { state: { leadTorolve: true } });
      qc.removeQueries({ queryKey: ['lead', lead.id] });
      qc.removeQueries({ queryKey: ['esemenyek', lead.id] });
      // A lead a listán kívül az elemzésben, a kiküldésekben és a minőség oldalon is szerepelhet: minden lekérdezés elavult.
      await qc.invalidateQueries();
    },
  });

  const cim = [lead.iranyitoszam, lead.terulet?.nev, [lead.utca, lead.hazszam].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  function bezar() {
    if (torles.isPending) return;
    setNyitva(false);
    setMezoHiba(null);
    torles.reset();
  }

  function megerosit() {
    if (ok === '') { setMezoHiba('Válaszd ki a törlés okát.'); return; }
    if (ok === 'egyeb' && megjegyzes.trim() === '') { setMezoHiba('Egyéb oknál a megjegyzés kötelező.'); return; }
    setMezoHiba(null);
    torles.mutate();
  }

  return (
    <Kartya>
      <h2 className="mb-1 text-[17px] font-medium tracking-[-0.015em]">Lead törlése</h2>
      <p className="mb-3 text-sm text-szoveg-halk">Csak a super admin törölhet. A törlés végleges, a nyoma személyes adat nélkül megmarad a naplóban.</p>
      <Gomb valtozat="veszely" onClick={() => setNyitva(true)}>Lead törlése</Gomb>
      <Parbeszed nyitva={nyitva} cim="Lead törlése" onBezar={bezar}>
        <p>Biztosan törlöd ezt a leadet? A lead, az idővonala és a jegyzetei véglegesen törlődnek, nem állítható vissza.</p>
        <p className="wrap-anywhere rounded-xl border border-vonal px-3.5 py-2.5 text-sm">
          <strong className="font-medium">{lead.nev}</strong>
          {cim && <><br />{cim}</>}
        </p>
        <Valaszto label="A törlés oka" value={ok} onChange={(e) => { setOk(e.target.value as Ok | ''); setMezoHiba(null); }}>
          <option value="">Válassz okot…</option>
          {OKOK.map((o) => <option key={o.kod} value={o.kod}>{o.cimke}</option>)}
        </Valaszto>
        {ok === 'teszt' && <p className="text-sm text-szoveg-halk">A lead levél- és SMS-naplója is törlődik, így nem számít bele a számlázásba.</p>}
        <p className="text-sm text-szoveg-halk">A megjegyzés a naplóban marad: nevet, telefonszámot, e-mail-címet vagy címet ne írj bele.</p>
        <Mezo
          label={ok === 'egyeb' ? 'Megjegyzés (kötelező)' : 'Megjegyzés (nem kötelező)'}
          multiline
          maxLength={500}
          value={megjegyzes}
          onChange={(e) => { setMegjegyzes(e.target.value); setMezoHiba(null); }}
          hiba={mezoHiba}
        />
        <HibaUzenet hiba={torles.error} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Gomb valtozat="masodlagos" onClick={bezar}>Mégse</Gomb>
          <Gomb valtozat="veszely" toltes={torles.isPending} onClick={megerosit}>Végleges törlés</Gomb>
        </div>
      </Parbeszed>
    </Kartya>
  );
}
